import { Operator } from 'apis/business/setting/operator/types';
import { TransferOperator, TransferSource, TransferStrategy, TransferType } from 'apis/business/agent/action/types';

export interface TransferTypeFormSnapshot {
  sources: TransferSource[];
  agent_token?: string;
  agent_name?: string;
  location_id?: string | number;
  transfer_strategy?: TransferStrategy;
  transfer_mode?: string;
  expandedOperatorIndex?: number | null;
}

export const TRANSFER_TYPE_OPTIONS = [
  { label: 'Operator', value: 'operator' },
  { label: 'AI Agent', value: 'agent' },
  { label: 'Group', value: 'group' }
] as const;

export const RINGING_STRATEGY_OPTIONS = [
  { label: 'Simultaneous', value: 'simultaneous' },
  { label: 'Sequential', value: 'sequential' }
] as const;

export const EMPTY_TRANSFER_SOURCE: TransferSource = {
  target_id: undefined,
  target_name: undefined,
  target_type: undefined,
  target_value: undefined,
  availability_schedule: undefined,
  holidays: undefined
};

/** Cleared source for form resets — empty strings overwrite stale react-hook-form values. */
export const CLEARED_TRANSFER_SOURCE: TransferSource = {
  target_id: '',
  target_name: '',
  target_type: '' as TransferSource['target_type'],
  target_value: '',
  availability_schedule: undefined,
  holidays: []
};

export function getSourcesForTransferType(transferType: string): TransferSource[] {
  if (transferType === 'agent') {
    return [{ ...CLEARED_TRANSFER_SOURCE, target_type: 'agent' }];
  }
  return [{ ...CLEARED_TRANSFER_SOURCE }];
}

function cloneTransferSources(sources: TransferSource[] | undefined): TransferSource[] {
  if (!sources?.length) {
    return getSourcesForTransferType('operator');
  }

  return sources.map((source) => ({
    ...source,
    availability_schedule: source.availability_schedule ? { ...source.availability_schedule } : undefined,
    holidays: source.holidays ? [...source.holidays] : source.holidays
  }));
}

export function captureTransferTypeSnapshot(
  details: Record<string, unknown>,
  expandedOperatorIndex?: number | null
): TransferTypeFormSnapshot {
  const sources = (details.sources as TransferSource[] | undefined) || [];

  return {
    sources: cloneTransferSources(sources),
    agent_token: details.agent_token as string | undefined,
    agent_name: details.agent_name as string | undefined,
    location_id: details.location_id as string | number | undefined,
    transfer_strategy: details.transfer_strategy as TransferStrategy | undefined,
    transfer_mode: details.transfer_mode as string | undefined,
    expandedOperatorIndex
  };
}

export function getDefaultTransferTypeState(transferType: string, locationId?: string): TransferTypeFormSnapshot {
  const snapshot: TransferTypeFormSnapshot = {
    sources: getSourcesForTransferType(transferType),
    agent_token: '',
    agent_name: '',
    location_id: '',
    transfer_strategy: undefined,
    expandedOperatorIndex: null
  };

  if (transferType === 'agent' && locationId) {
    snapshot.location_id = locationId;
  }

  if (transferType === 'group') {
    snapshot.transfer_mode = 'warm';
    snapshot.expandedOperatorIndex = 0;
  }

  return snapshot;
}

export function applyTransferTypeSnapshot(
  snapshot: TransferTypeFormSnapshot,
  parent: string,
  setValue: (name: string, value: unknown) => void,
  replace: (sources: TransferSource[]) => void
): void {
  replace(snapshot.sources);
  setValue(`${parent}.agent_token`, snapshot.agent_token ?? '');
  setValue(`${parent}.agent_name`, snapshot.agent_name ?? '');
  setValue(`${parent}.location_id`, snapshot.location_id ?? '');
  setValue(`${parent}.transfer_strategy`, snapshot.transfer_strategy);

  if (snapshot.transfer_mode !== undefined) {
    setValue(`${parent}.transfer_mode`, snapshot.transfer_mode);
  }
}

/** @deprecated Use EMPTY_TRANSFER_SOURCE */
export const EMPTY_TRANSFER_OPERATOR: TransferOperator = {
  operator_id: undefined,
  target_type: undefined
};

export function hasTransferTargetId(targetId?: TransferSource['target_id'] | null): boolean {
  return targetId !== undefined && targetId !== null && targetId !== '';
}

export function isTransferSourceComplete(entry?: TransferSource | null): boolean {
  return Boolean(hasTransferTargetId(entry?.target_id) && entry?.target_type && entry?.target_value);
}

/** @deprecated Use isTransferSourceComplete */
export function isTransferOperatorComplete(entry?: TransferOperator | null): boolean {
  return Boolean(hasTransferTargetId(entry?.operator_id) && entry?.target_type);
}

export function getPreferredContactType(
  operator?: {
    phone_number?: string;
    sip_uri?: string;
    extension?: string | number;
  } | null
): 'phone' | 'sip' | 'extension' | null {
  if (!operator) return null;
  if (operator.phone_number) return 'phone';
  if (operator.sip_uri) return 'sip';
  if (operator.extension != null && operator.extension !== '') return 'extension';
  return null;
}

export function getOperatorTargetValue(
  operator?: {
    phone_number?: string;
    sip_uri?: string;
    extension?: string | number;
  } | null,
  targetType?: string
): string {
  switch (targetType?.toLowerCase()) {
    case 'phone':
      return operator?.phone_number || '';
    case 'sip':
      return operator?.sip_uri || '';
    case 'extension':
      return operator?.extension != null ? String(operator.extension) : '';
    default:
      return '';
  }
}

export function buildSourceFromOperator(operator: Operator, targetType: string): TransferSource {
  return {
    target_id: operator.id,
    target_name: getOperatorDisplayName(operator),
    target_type: targetType as TransferSource['target_type'],
    target_value: getOperatorTargetValue(operator, targetType),
    availability_schedule: operator.working_hours ? { ...operator.working_hours } : undefined,
    holidays: operator.holidays ? [...operator.holidays] : []
  };
}

export function buildSourceFromAgent(agent: {
  id?: number | string;
  agent_token?: string;
  name?: string;
  agent_name?: string;
}): TransferSource {
  return {
    target_id: agent.id,
    target_name: agent.name || agent.agent_name || '',
    target_type: 'agent',
    target_value: agent.agent_token || '',
    availability_schedule: undefined,
    holidays: []
  };
}

export function getInitialExpandedOperatorIndex(sources: TransferSource[]): number | null {
  if (sources.length === 0) return 0;
  const incompleteIndex = sources.findIndex((source) => !isTransferSourceComplete(source));
  if (incompleteIndex >= 0) return incompleteIndex;
  if (sources.length === 1) return 0;
  return null;
}

export function isTransferSourceArray(sources: unknown): sources is TransferSource[] {
  return Array.isArray(sources) && sources.length > 0 && typeof sources[0] === 'object';
}

export function isTransferOperatorArray(operators: unknown): operators is TransferOperator[] {
  return Array.isArray(operators) && operators.length > 0 && typeof operators[0] === 'object';
}

function legacyOperatorToSource(op: TransferOperator): TransferSource {
  return {
    target_id: op.operator_id,
    target_type: op.target_type,
    target_name: undefined,
    target_value: undefined,
    availability_schedule: undefined,
    holidays: undefined
  };
}

function normalizeAgentSourceEntry(source: TransferSource): TransferSource {
  return {
    target_id: source.target_id,
    target_name: source.target_name,
    target_type: source.target_type,
    target_value: source.target_value
  };
}

function normalizeOperatorSourceEntry(source: TransferSource): TransferSource {
  return {
    target_id: source.target_id,
    target_type: source.target_type
  };
}

export function hydrateTransferCallDetails(details: Record<string, any> = {}): Record<string, any> {
  const hydrated = { ...details };

  if (hydrated.ringing_strategy && !hydrated.transfer_strategy) {
    hydrated.transfer_strategy = hydrated.ringing_strategy;
  }

  const explicitTransferType = hydrated.transfer_type as TransferType | undefined;

  const isAgentTransfer =
    explicitTransferType === 'agent' ||
    (explicitTransferType !== 'operator' &&
      explicitTransferType !== 'group' &&
      ((hydrated.agent_token && !isTransferSourceArray(hydrated.sources)) ||
        (isTransferSourceArray(hydrated.sources) && hydrated.sources[0]?.target_type === 'agent')));

  if (isAgentTransfer) {
    hydrated.transfer_type = 'agent';
    if (!isTransferSourceArray(hydrated.sources) && hydrated.agent_token) {
      hydrated.sources = [
        {
          target_id: hydrated.target_id ?? hydrated.agent_token,
          target_name: hydrated.agent_name || '',
          target_type: 'agent',
          target_value: hydrated.agent_token,
          availability_schedule: undefined,
          holidays: []
        }
      ];
    } else if (!isTransferSourceArray(hydrated.sources)) {
      hydrated.sources = [{ ...EMPTY_TRANSFER_SOURCE, target_type: 'agent' }];
    } else if (hydrated.sources[0]?.target_type === 'agent') {
      hydrated.agent_token = hydrated.sources[0].target_value;
      hydrated.agent_name = hydrated.sources[0].target_name || hydrated.agent_name;
    }
    delete hydrated.target_type;
    delete hydrated.target_value;
    delete hydrated.operators;
    return hydrated;
  }

  if (hydrated.transfer_type === 'group' || hydrated.target_type === 'group') {
    hydrated.transfer_type = 'group';
    if (!isTransferSourceArray(hydrated.sources)) {
      if (isTransferOperatorArray(hydrated.operators)) {
        hydrated.sources = hydrated.operators.map(legacyOperatorToSource);
      } else {
        const legacyIds: number[] = Array.isArray(hydrated.operator_ids) ? hydrated.operator_ids : [];
        if (legacyIds.length > 0) {
          hydrated.sources = legacyIds.map((id) => ({ ...EMPTY_TRANSFER_SOURCE, target_id: id }));
        } else if (!Array.isArray(hydrated.sources)) {
          hydrated.sources = [{ ...EMPTY_TRANSFER_SOURCE }];
        }
      }
    }
    delete hydrated.target_type;
    delete hydrated.target_value;
    delete hydrated.operators;
    return hydrated;
  }

  hydrated.transfer_type = (hydrated.transfer_type as TransferType) || 'operator';
  delete hydrated.agent_token;
  delete hydrated.agent_name;

  if (isTransferSourceArray(hydrated.sources) && hydrated.sources[0]?.target_type === 'agent') {
    hydrated.sources = [{ ...EMPTY_TRANSFER_SOURCE }];
  } else if (!isTransferSourceArray(hydrated.sources)) {
    if (isTransferOperatorArray(hydrated.operators)) {
      hydrated.sources = hydrated.operators.map(legacyOperatorToSource);
    } else if (hydrated.operator_id) {
      hydrated.sources = [
        {
          ...EMPTY_TRANSFER_SOURCE,
          target_id: hydrated.operator_id,
          target_type: hydrated.target_type
        }
      ];
    } else {
      hydrated.sources = [{ ...EMPTY_TRANSFER_SOURCE }];
    }
  }

  delete hydrated.target_type;
  delete hydrated.target_value;
  delete hydrated.operators;
  return hydrated;
}

export function normalizeTransferCallDetails(details: Record<string, any> = {}): Record<string, any> {
  const normalized = { ...details };
  const transferType = (normalized.transfer_type as TransferType) || 'operator';
  const sources = (normalized.sources as TransferSource[] | undefined) || [];
  const ringingStrategy = normalized.transfer_strategy || normalized.ringing_strategy;
  const agentToken = normalized.agent_token;
  const agentName = normalized.agent_name;
  const legacyTargetId = normalized.target_id;

  delete normalized.target_type;
  delete normalized.target_value;
  delete normalized.operators;
  delete normalized.operator_id;
  delete normalized.operator_ids;
  delete normalized.transfer_strategy;
  delete normalized.agent_token;
  delete normalized.agent_name;
  delete normalized.availability_schedule;

  if (transferType === 'agent') {
    const agentSource = sources[0];
    if (agentSource?.target_value) {
      normalized.sources = [normalizeAgentSourceEntry(agentSource)];
    } else if (agentToken) {
      normalized.sources = [
        normalizeAgentSourceEntry({
          target_id: legacyTargetId ?? agentToken,
          target_name: agentName || '',
          target_type: 'agent',
          target_value: agentToken
        })
      ];
    } else {
      normalized.sources = [];
    }
    return normalized;
  }

  if (transferType === 'group') {
    normalized.transfer_mode = 'warm';
    if (ringingStrategy) {
      normalized.ringing_strategy = ringingStrategy;
    }
    normalized.sources = sources
      .filter((source) => hasTransferTargetId(source?.target_id) && source?.target_type)
      .map(normalizeOperatorSourceEntry);
    return normalized;
  }

  const source0 = sources[0];
  const isOperatorSource = source0?.target_type && source0.target_type !== 'agent';
  normalized.sources =
    isOperatorSource && hasTransferTargetId(source0?.target_id) && source0?.target_type
      ? [normalizeOperatorSourceEntry(source0)]
      : [];
  return normalized;
}

export function getTransferModeOptions(targetType?: string) {
  switch (targetType) {
    case 'extension':
      return [{ label: 'Warm Transfer', value: 'warm' }];
    case 'sip':
      return [{ label: 'Cold Transfer', value: 'cold' }];
    default:
      return [
        { label: 'Cold Transfer', value: 'cold' },
        { label: 'Warm Transfer', value: 'warm' }
      ];
  }
}

export function getTargetTypeLabel(targetType?: string): string {
  switch (targetType?.toLowerCase()) {
    case 'phone':
      return 'Phone Number';
    case 'sip':
      return 'sip';
    case 'extension':
      return 'Extension';
    case 'agent':
      return 'AI Agent';
    default:
      return 'Operator';
  }
}

export function getOperatorDisplayName(operator?: {
  first_name?: string;
  last_name?: string;
  job_title?: string;
}): string {
  if (!operator) return '';
  const name = [operator.first_name, operator.last_name].filter(Boolean).join(' ').trim();
  if (operator.job_title) {
    return name ? `${name} - ${operator.job_title}` : operator.job_title;
  }
  return name;
}

export function getTargetDetail(
  operator:
    | {
        first_name?: string;
        last_name?: string;
        job_title?: string;
        phone_number?: string;
        sip_uri?: string;
        extension?: string | number;
      }
    | null
    | undefined,
  targetType?: string
): string {
  if (!operator) return 'Not configured';

  const displayName = getOperatorDisplayName(operator);

  switch (targetType?.toLowerCase()) {
    case 'phone':
      return displayName ? `${displayName} (${operator.phone_number})` : operator.phone_number || 'Not configured';
    case 'sip':
      return displayName ? `${displayName} - ${operator.sip_uri}` : operator.sip_uri || 'Not configured';
    case 'extension':
      return displayName ? `${displayName} (${operator.extension})` : String(operator.extension || 'Not configured');
    default:
      return displayName || 'Not configured';
  }
}

export function getSourceDisplayDetail(source?: TransferSource): string {
  if (!source) return 'Not configured';
  if (source.target_type === 'agent') {
    return source.target_name
      ? `${source.target_name} (${source.target_value})`
      : source.target_value || 'Not configured';
  }
  const label = getTargetTypeLabel(source.target_type);
  if (source.target_name && source.target_value) {
    return `${source.target_name} (${label}: ${source.target_value})`;
  }
  return source.target_value || source.target_name || 'Not configured';
}
